import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useBranding } from '../contexts/BrandingContext';
import { useAuthPageBackground, AUTH_BG_CLASSES } from '../hooks/useAuthPageBackground';
import { authAPI } from '../lib/api';
import { ArrowLeft, Loader2, CheckCircle, XCircle, Lock, Eye, EyeOff } from 'lucide-react';

type PageState = 'validating' | 'invalid' | 'form' | 'success';

export default function ResetPassword() {
  const { branding, isLoading: brandingLoading } = useBranding();
  const authBackground = useAuthPageBackground();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [pageState, setPageState] = useState<PageState>('validating');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(3);

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setPageState('invalid');
      return;
    }

    let cancelled = false;
    authAPI.validateResetToken(token)
      .then(() => {
        if (!cancelled) setPageState('form');
      })
      .catch(() => {
        if (!cancelled) setPageState('invalid');
      });

    return () => { cancelled = true; };
  }, [token]);

  // Auto-redirect after success
  useEffect(() => {
    if (pageState !== 'success') return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/login', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [pageState, navigate]);

  const passwordRequirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'Contains a number', met: /\d/.test(password) },
    { label: 'Contains an uppercase letter', met: /[A-Z]/.test(password) },
    { label: 'Contains a lowercase letter', met: /[a-z]/.test(password) },
  ];

  const allRequirementsMet = passwordRequirements.every((r) => r.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRequirementsMet) {
      setError('Please meet all password requirements.');
      return;
    }
    if (!passwordsMatch) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await authAPI.resetPassword({
        token,
        new_password: password,
        confirm_password: confirmPassword,
      });
      setPageState('success');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: Record<string, string[] | string> } };
      if (axiosErr.response?.data) {
        const data = axiosErr.response.data;
        const firstKey = Object.keys(data)[0];
        if (firstKey) {
          const val = data[firstKey];
          setError(Array.isArray(val) ? val[0] : String(val));
        } else {
          setError('Failed to reset password. Please try again.');
        }
      } else {
        setError('Unable to connect to the server. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (brandingLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const renderLogo = () => (
    <div className="text-center mb-8">
      {/* CR-6243: light logo regardless of theme — the card is `bg-white`
          with no dark variant. See Login.tsx. */}
      {branding.companyLogo ? (
        <img
          src={branding.companyLogo}
          alt={branding.companyName}
          className="mx-auto mb-4 max-h-20 max-w-[200px] object-contain"
        />
      ) : (
        <div
          className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
          style={{ backgroundColor: branding.loginColor }}
        >
          <svg
            className="w-10 h-10 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (pageState) {
      case 'validating':
        return (
          <div className="text-center py-8">
            <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-gray-400" />
            <p className="text-gray-600">Validating your reset link...</p>
          </div>
        );

      case 'invalid':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-red-100 mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Invalid or Expired Link
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              This password reset link is invalid or has expired.
              Please request a new one.
            </p>
            <Link
              to="/forgot-password"
              className="inline-flex items-center justify-center w-full py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white hover:opacity-90 transition-colors"
              style={{ backgroundColor: branding.buttonColor }}
            >
              Request New Reset Link
            </Link>
            <div className="mt-4">
              <Link
                to="/login"
                className="inline-flex items-center text-sm font-medium hover:opacity-80"
                style={{ color: branding.buttonColor }}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to login
              </Link>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center w-16 h-16 rounded-full bg-green-100 mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Password Reset Successfully
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Your password has been updated. Redirecting to login in {countdown}s...
            </p>
            <Link
              to="/login"
              className="inline-flex items-center text-sm font-medium hover:opacity-80"
              style={{ color: branding.buttonColor }}
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              Go to login now
            </Link>
          </div>
        );

      case 'form':
        return (
          <>
            <h1 className="text-3xl font-bold text-gray-900 text-center">Set New Password</h1>
            <p className="text-gray-600 mt-2 text-center mb-8">
              Choose a strong password for your account
            </p>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* New Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  New Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError(null);
                    }}
                    required
                    autoComplete="new-password"
                    autoFocus
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Enter new password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              {password.length > 0 && (
                <div className="space-y-1.5">
                  {passwordRequirements.map((req) => (
                    <div key={req.label} className="flex items-center text-sm">
                      <div
                        className={`w-4 h-4 rounded-full mr-2 flex items-center justify-center ${
                          req.met ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {req.met ? (
                          <CheckCircle className="w-3.5 h-3.5" />
                        ) : (
                          <div className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        )}
                      </div>
                      <span className={req.met ? 'text-green-700' : 'text-gray-500'}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirm-password"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    id="confirm-password"
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      if (error) setError(null);
                    }}
                    required
                    autoComplete="new-password"
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                    placeholder="Confirm new password"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="mt-1.5 text-sm text-red-600">Passwords do not match</p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading || !allRequirementsMet || !passwordsMatch}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:cursor-not-allowed transition-colors hover:opacity-90"
                style={{
                  backgroundColor: branding.buttonColor,
                  opacity: loading || !allRequirementsMet || !passwordsMatch ? 0.6 : 1,
                }}
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Resetting...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center text-sm font-medium hover:opacity-80"
                style={{ color: branding.buttonColor }}
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back to login
              </Link>
            </div>
          </>
        );
    }
  };

  return (
    <div
      className={`min-h-screen flex items-center justify-center ${AUTH_BG_CLASSES}`}
      style={authBackground}
    >
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-lg shadow-xl p-8">
          {renderLogo()}
          {renderContent()}
        </div>

        {/* Powered-by footer */}
        <div className="mt-8 flex items-center justify-center gap-2 text-sm text-gray-500">
          <span>Powered by</span>
          <img src="/valuechain-logo.png" alt="Valuechain" className="h-14 w-auto" />
        </div>
      </div>
    </div>
  );
}
